import { FC, useEffect, useRef, useState } from "react"
import styled from "styled-components"
import {
    colorAttentionRed50,
    colorFunctionalGray30,
    colorFunctionalGray70,
    colorMiscGray90,
    IconArrowDown,
    IconButton,
    IconTrash,
    iconSizeM,
    iconSizeXs,
    colorLayoutBorder,
} from "@rhumbix/rmbx_design_system_web"
import { iTimecardTemplate } from "./timecard-template-utils"

export type TimecardTemplatePickerProps = {
    id?: string
    templates: iTimecardTemplate[]
    selectedTemplate: iTimecardTemplate | null
    onSelect: (template: iTimecardTemplate | null) => void
    /** Omit to render the picker without a delete affordance on each row (e.g. when picking a
     *  template to overwrite, where deleting isn't a relevant action). */
    onRequestDelete?: (template: iTimecardTemplate) => void
}

/**
 * A plain-text toggle that opens a small popover listing the user's saved timecard templates --
 * the same open/close/outside-click pattern as the Weekly View's ChecklistButton, but without
 * checkboxes. Hovering a row reveals a delete icon when `onRequestDelete` is supplied; clicking
 * the currently-selected template again clears the selection.
 */
const TimecardTemplatePicker: FC<TimecardTemplatePickerProps> = props => {
    const [isOpen, setIsOpen] = useState(false)
    const containerRef = useRef<HTMLDivElement>(null)
    const isDisabled = props.templates.length === 0

    useEffect(() => {
        const handleClickOutside = (event: MouseEvent) => {
            if (containerRef.current && !containerRef.current.contains(event.target as Node)) setIsOpen(false)
        }
        document.addEventListener("mousedown", handleClickOutside)
        return () => document.removeEventListener("mousedown", handleClickOutside)
    }, [])

    const handleSelect = (template: iTimecardTemplate) => {
        props.onSelect(props.selectedTemplate?.id === template.id ? null : template)
        setIsOpen(false)
    }

    return (
        <PickerContainer ref={containerRef}>
            <PickerLabel id={props.id} data-testid={props.id} type="button" disabled={isDisabled} onClick={() => setIsOpen(!isOpen)}>
                <PickerSwatch />
                <PickerLabelText>{props.selectedTemplate?.name ?? "Select Template"}</PickerLabelText>
                <IconArrowDown height={iconSizeXs} width={iconSizeXs} color={isDisabled ? colorFunctionalGray30 : colorFunctionalGray70} />
            </PickerLabel>
            {isOpen && !isDisabled && (
                <PickerWindow>
                    <PickerList>
                        {props.templates.map(template => (
                            <PickerRow key={template.id} $isSelected={props.selectedTemplate?.id === template.id}>
                                <PickerChoice type="button" onClick={() => handleSelect(template)}>
                                    {template.name}
                                </PickerChoice>
                                {props.onRequestDelete && (
                                    <DeleteTemplateButton
                                        type="button"
                                        aria-label={`Delete ${template.name}`}
                                        className="delete-template-button"
                                        onClick={event => {
                                            event.stopPropagation()
                                            setIsOpen(false)
                                            props.onRequestDelete?.(template)
                                        }}
                                    >
                                        <IconTrash color={colorAttentionRed50} height={iconSizeM} width={iconSizeM} />
                                    </DeleteTemplateButton>
                                )}
                            </PickerRow>
                        ))}
                    </PickerList>
                    <PickerFooter>
                        <IconButton text={"Done"} theme={"rightRailPrimary"} onClick={() => setIsOpen(false)} />
                    </PickerFooter>
                </PickerWindow>
            )}
        </PickerContainer>
    )
}

export default TimecardTemplatePicker

const PickerContainer = styled.div`
    position: relative;
    display: inline-block;
`

const PickerLabel = styled.button`
    display: flex;
    align-items: center;
    gap: 8px;
    background: none;
    border: none;
    padding: 0;
    color: ${colorFunctionalGray70};
    font-family: Roboto;
    font-size: 14px;
    cursor: pointer;

    &:disabled {
        color: ${colorFunctionalGray30};
        cursor: not-allowed;
    }
`

const PickerSwatch = styled.span`
    flex-shrink: 0;
    width: 16px;
    height: 16px;
    border-radius: 4px;
    background-color: ${colorFunctionalGray70};

    ${PickerLabel}:disabled & {
        background-color: ${colorFunctionalGray30};
    }
`

const PickerLabelText = styled.span`
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
    margin-top: 4px;
`

const PickerWindow = styled.div`
    position: absolute;
    top: 100%;
    left: 0;
    margin-top: 4px;
    min-width: 220px;
    min-height: 300px;
    display: flex;
    flex-direction: column;
    background-color: #fff;
    border: 1px solid rgba(0, 0, 0, 0.15);
    box-shadow: 0 6px 12px rgba(0, 0, 0, 0.175);
    z-index: 10;
`

const PickerList = styled.ul`
    flex: 1;
    list-style: none;
    margin: 0;
    padding: 0;
    max-height: 250px;
    overflow-y: auto;
`

const PickerFooter = styled.div`
    display: flex;
    justify-content: flex-end;
    border-top: 1px solid ${colorFunctionalGray30};
    padding: 10px;
`

const PickerRow = styled.li<{ $isSelected: boolean }>`
    display: flex;
    align-items: center;
    justify-content: space-between;
    padding: 2px 12px;
    background-color: ${props => (props.$isSelected ? "rgba(0, 0, 0, 0.04)" : "transparent")};
    border-bottom: 1px solid ${colorLayoutBorder};

    .delete-template-button {
        visibility: hidden;
    }

    &:hover {
        background-color: rgba(0, 0, 0, 0.08);

        .delete-template-button {
            visibility: visible;
        }
    }

    &:not(:last-child) {
        border-bottom: 1px solid ${colorFunctionalGray30};
    }
`

const PickerChoice = styled.button`
    flex: 1;
    text-align: left;
    background: none;
    border: none;
    padding: 0;
    cursor: pointer;
    font-family: Roboto;
    font-size: 14px;
    color: ${colorMiscGray90};
`

const DeleteTemplateButton = styled.button`
    display: flex;
    align-items: center;
    background: none;
    border: none;
    padding: 0;
    margin-left: 8px;
    cursor: pointer;
    justify-content: flex-end;
`
